import React, { useState, useEffect } from "react";
import {
  FaSearch,
  FaCopy,
  FaTimes,
  FaInfoCircle,
  FaCog,
  FaGlobe,
  FaFileAlt,
  FaBan,
  FaSync,
  FaUndo,
} from "react-icons/fa";
import "./SearchQueryGenerator.css";

const SearchQueryGenerator = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [generatedQuery, setGeneratedQuery] = useState("");
  const [searchHistory, setSearchHistory] = useState([]);
  const [showTips, setShowTips] = useState(false);
  const [copiedQuery, setCopiedQuery] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showGeneratedQuery, setShowGeneratedQuery] = useState(false);
  const [templateFields, setTemplateFields] = useState({
    sites: "",
    filetypes: "",
    exclusions: "",
    keywords: "",
    timeframes: "",
    operators: "",
    customQuery: "",
  });
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Auto-update generated query when template fields change
  useEffect(() => {
    if (
      selectedTemplate &&
      (templateFields.sites.trim() ||
        templateFields.filetypes.trim() ||
        templateFields.exclusions.trim() ||
        templateFields.keywords.trim() ||
        templateFields.timeframes.trim() ||
        templateFields.operators.trim() ||
        templateFields.customQuery.trim())
    ) {
      const updatedQuery = generateAdvancedQuery(searchQuery, selectedTemplate);
      setGeneratedQuery(updatedQuery);
      // Don't set showGeneratedQuery here - only show when user explicitly clicks Generate
    }
  }, [templateFields, selectedTemplate, searchQuery]);

  // Predefined search templates
  const searchTemplates = [
    {
      name: "Index of",
      description: "Directory listings",
      query:
        'intitle:"index of" -inurl:(jsp|pl|php|html|aspx|htm|cf|shtml) -inurl:(hypem|unknownsecret|sirens|writeups|trimediacentral|articlescentral|listen77|mp3raid|mp3toss|mp3drug|theindexof|index_of|wallywashis|indexofmp3)',
      category: "Listing",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "mega.nz",
        "onedrive.live.com",
      ],
      filetypes: ["*", "zip", "rar", "7z", "tar.gz"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "DB files",
      description: "SQL dumps & backups",
      query: 'filetype:sql intext:"CREATE TABLE" -inurl:php -inurl:asp',
      category: "Database",
      sites: ["pastebin.com", "gist.github.com", "jsfiddle.net"],
      filetypes: ["sql", "db", "sqlite", "bak", "backup"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Configs",
      description: "Config files",
      query:
        'filetype:conf intext:"password" OR intext:"admin" OR intext:"root"',
      category: "Config",
      sites: ["github.com", "gitlab.com", "bitbucket.org"],
      filetypes: ["conf", "config", "ini", "cfg", "env"],
      exclusions: ["-site:stackoverflow.com", "-inurl:admin", "-inurl:login"],
    },
    {
      name: "Backups",
      description: "Backup archives",
      query: "filetype:bak OR filetype:backup OR filetype:old OR filetype:tmp",
      category: "Backup",
      sites: ["drive.google.com", "dropbox.com", "mega.nz"],
      filetypes: ["bak", "backup", "old", "tmp", "zip", "tar.gz"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Logs",
      description: "Error logs",
      query:
        'filetype:log intext:"error" OR intext:"warning" OR intext:"failed"',
      category: "Logs",
      sites: ["pastebin.com", "gist.github.com", "jsfiddle.net"],
      filetypes: ["log", "txt", "err", "out"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Docs",
      description: "Common docs",
      query: "filetype:pdf OR filetype:doc OR filetype:xls OR filetype:ppt",
      category: "Documents",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "onedrive.live.com",
        "slideshare.net",
      ],
      filetypes: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Code",
      description: "Source files",
      query: "filetype:java OR filetype:py OR filetype:cpp OR filetype:js",
      category: "Code",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["java", "py", "cpp", "js", "php", "rb", "go"],
      exclusions: ["-site:stackoverflow.com", "-inurl:admin", "-inurl:login"],
    },
    {
      name: "Media",
      description: "Audio/Video/Zip",
      query: "filetype:mp3 OR filetype:mp4 OR filetype:avi OR filetype:zip",
      category: "Media",
      sites: ["drive.google.com", "dropbox.com", "mega.nz", "mediafire.com"],
      filetypes: ["mp3", "mp4", "avi", "zip", "rar", "7z", "mkv"],
      exclusions: [
        "-site:github.com",
        "-site:stackoverflow.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Credentials",
      description: "Login & password files",
      query:
        'intext:"username" OR intext:"password" OR intext:"login" OR intext:"admin" OR intext:"credential" OR intext:"auth"',
      category: "Security",
      sites: [
        "pastebin.com",
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "txt",
        "log",
        "csv",
        "json",
        "xml",
        "env",
        "conf",
        "config",
        "ini",
        "cfg",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:login",
        "-inurl:admin",
        "-inurl:help",
      ],
      keywords: [
        "login",
        "password",
        "credential",
        "authentication",
        "user",
        "admin",
        "root",
        "sudo",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "API Keys",
      description: "API tokens & keys",
      query:
        'intext:"api_key" OR intext:"api_token" OR intext:"access_token" OR intext:"secret_key" OR intext:"private_key" OR intext:"client_secret"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "env",
        "json",
        "yaml",
        "yml",
        "conf",
        "config",
        "ini",
        "cfg",
        "properties",
        "xml",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "api",
        "token",
        "key",
        "secret",
        "authentication",
        "oauth",
        "bearer",
        "jwt",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Emails",
      description: "Email addresses & lists",
      query:
        'intext:"@gmail.com" OR intext:"@yahoo.com" OR intext:"@hotmail.com" OR intext:"@outlook.com"',
      category: "Data",
      sites: ["pastebin.com", "github.com", "drive.google.com", "dropbox.com"],
      filetypes: ["txt", "csv", "xls", "xlsx", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Phone Numbers",
      description: "Phone number lists",
      query: 'intext:"+1-" OR intext:"+44-" OR intext:"+33-" OR intext:"+49-"',
      category: "Data",
      sites: ["pastebin.com", "github.com", "drive.google.com", "dropbox.com"],
      filetypes: ["txt", "csv", "xls", "xlsx", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "SSH Keys",
      description: "SSH private keys",
      query:
        'intext:"-----BEGIN OPENSSH PRIVATE KEY-----" OR intext:"-----BEGIN RSA PRIVATE KEY-----" OR intext:"-----BEGIN DSA PRIVATE KEY-----" OR intext:"-----BEGIN EC PRIVATE KEY-----"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "key",
        "pem",
        "ppk",
        "txt",
        "pub",
        "id_rsa",
        "id_dsa",
        "id_ecdsa",
        "id_ed25519",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "ssh",
        "private key",
        "public key",
        "openssh",
        "rsa",
        "dsa",
        "ecdsa",
        "ed25519",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Certificates",
      description: "SSL/TLS certificates",
      query:
        'intext:"-----BEGIN CERTIFICATE-----" OR intext:"-----BEGIN X509 CERTIFICATE-----" OR intext:"-----BEGIN TRUSTED CERTIFICATE-----" OR intext:"-----BEGIN PKCS7-----"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["crt", "pem", "cer", "der", "p12", "pfx", "key", "csr"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "ssl",
        "tls",
        "certificate",
        "x509",
        "pkcs",
        "trusted",
        "ca",
        "root",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "WordPress",
      description: "WordPress files & configs",
      query:
        'intext:"wp-config.php" OR intext:"wp-content" OR intext:"wordpress"',
      category: "CMS",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["php", "txt", "log", "sql", "bak"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Drupal",
      description: "Drupal files & configs",
      query: 'intext:"drupal" OR intext:"settings.php" OR intext:"drupal.org"',
      category: "CMS",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["php", "txt", "log", "sql", "bak"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Joomla",
      description: "Joomla files & configs",
      query:
        'intext:"joomla" OR intext:"configuration.php" OR intext:"joomla.org"',
      category: "CMS",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["php", "txt", "log", "sql", "bak"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Magento",
      description: "Magento e-commerce files",
      query: 'intext:"magento" OR intext:"local.xml" OR intext:"magento.com"',
      category: "E-commerce",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["php", "xml", "txt", "log", "sql"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Shopify",
      description: "Shopify store files",
      query:
        'intext:"shopify" OR intext:"myshopify.com" OR intext:"shopify.com"',
      category: "E-commerce",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["liquid", "js", "css", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Docker",
      description: "Docker files & images",
      query:
        'intext:"dockerfile" OR intext:"docker-compose" OR intext:"docker.io"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "dockerhub.com"],
      filetypes: ["dockerfile", "yml", "yaml", "json", "sh"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Kubernetes",
      description: "K8s config files",
      query: 'intext:"kubernetes" OR intext:"k8s" OR intext:"apiVersion: v1"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "kubernetes.io"],
      filetypes: ["yml", "yaml", "json"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Terraform",
      description: "Terraform infrastructure files",
      query: 'intext:"terraform" OR intext:"provider" OR intext:"resource"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "terraform.io"],
      filetypes: ["tf", "tfvars", "hcl", "json"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Ansible",
      description: "Ansible playbooks",
      query: 'intext:"ansible" OR intext:"playbook" OR intext:"hosts:"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "ansible.com"],
      filetypes: ["yml", "yaml", "ini", "cfg"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Jenkins",
      description: "Jenkins CI/CD files",
      query: 'intext:"jenkins" OR intext:"pipeline" OR intext:"Jenkinsfile"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "jenkins.io"],
      filetypes: ["groovy", "xml", "yml", "yaml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "GitLab CI",
      description: "GitLab CI/CD files",
      query:
        'intext:".gitlab-ci.yml" OR intext:"gitlab-ci" OR intext:"stages:"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org"],
      filetypes: ["yml", "yaml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "GitHub Actions",
      description: "GitHub Actions workflows",
      query:
        'intext:".github/workflows" OR intext:"github-actions" OR intext:"on:"',
      category: "DevOps",
      sites: ["github.com", "gitlab.com", "bitbucket.org"],
      filetypes: ["yml", "yaml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "AWS Configs",
      description: "AWS configuration files",
      query: 'intext:"aws" OR intext:"amazon" OR intext:"access_key_id"',
      category: "Cloud",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["yml", "yaml", "json", "ini", "cfg"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Azure Configs",
      description: "Azure configuration files",
      query: 'intext:"azure" OR intext:"microsoft" OR intext:"tenant_id"',
      category: "Cloud",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["yml", "yaml", "json", "ini", "cfg"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "GCP Configs",
      description: "Google Cloud configs",
      query: 'intext:"gcp" OR intext:"google" OR intext:"project_id"',
      category: "Cloud",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["yml", "yaml", "json", "ini", "cfg"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "VPN Configs",
      description: "VPN configuration files",
      query: 'intext:"vpn" OR intext:"openvpn" OR intext:"wireguard"',
      category: "Network",
      sites: ["github.com", "gitlab.com", "bitbucket.org", "pastebin.com"],
      filetypes: ["ovpn", "conf", "config", "txt"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Proxy Lists",
      description: "Proxy server lists",
      query: 'intext:"proxy" OR intext:"socks" OR intext:"http_proxy"',
      category: "Network",
      sites: ["pastebin.com", "github.com", "drive.google.com", "dropbox.com"],
      filetypes: ["txt", "csv", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Tor Nodes",
      description: "Tor network nodes",
      query: 'intext:"tor" OR intext:"onion" OR intext:"exit node"',
      category: "Network",
      sites: ["pastebin.com", "github.com", "drive.google.com", "dropbox.com"],
      filetypes: ["txt", "csv", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
    },
    {
      name: "Malware Samples",
      description: "Malware analysis files",
      query:
        'intext:"malware" OR intext:"virus" OR intext:"trojan" OR intext:"ransomware" OR intext:"spyware" OR intext:"backdoor"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "exe",
        "dll",
        "bin",
        "txt",
        "log",
        "md",
        "py",
        "c",
        "cpp",
        "java",
        "jar",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "malware",
        "virus",
        "trojan",
        "ransomware",
        "spyware",
        "backdoor",
        "worm",
        "rootkit",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Exploit Code",
      description: "Security exploit code",
      query:
        'intext:"exploit" OR intext:"vulnerability" OR intext:"cve-" OR intext:"0day" OR intext:"zero day" OR intext:"proof of concept"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "rb",
        "sh",
        "ps1",
        "bat",
        "txt",
        "md",
        "json",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "exploit",
        "vulnerability",
        "cve",
        "0day",
        "zero day",
        "poc",
        "proof of concept",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "CTF Challenges",
      description: "Capture The Flag challenges",
      query:
        'intext:"ctf" OR intext:"flag" OR intext:"challenge" OR intext:"hackthebox" OR intext:"tryhackme" OR intext:"picoctf"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "txt",
        "py",
        "c",
        "cpp",
        "rb",
        "sh",
        "ps1",
        "md",
        "json",
        "zip",
        "rar",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "ctf",
        "flag",
        "challenge",
        "hackthebox",
        "tryhackme",
        "picoctf",
        "cyber",
        "hacking",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Penetration Testing",
      description: "Pentest tools & scripts",
      query:
        'intext:"penetration test" OR intext:"pentest" OR intext:"security audit" OR intext:"vulnerability assessment" OR intext:"security testing"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "sh",
        "rb",
        "ps1",
        "bat",
        "txt",
        "md",
        "json",
        "yml",
        "yaml",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "penetration test",
        "pentest",
        "security audit",
        "vulnerability assessment",
        "security testing",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Reverse Engineering",
      description: "RE tools & analysis",
      query:
        'intext:"reverse engineering" OR intext:"disassembly" OR intext:"decompiler" OR intext:"debugger" OR intext:"ida pro" OR intext:"ghidra"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "exe",
        "dll",
        "bin",
        "asm",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "reverse engineering",
        "disassembly",
        "decompiler",
        "debugger",
        "ida pro",
        "ghidra",
        "radare2",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Forensics Tools",
      description: "Digital forensics software",
      query:
        'intext:"forensics" OR intext:"memory dump" OR intext:"disk image" OR intext:"evidence" OR intext:"investigation" OR intext:"case file"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "exe",
        "dll",
        "bin",
        "jar",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "forensics",
        "memory dump",
        "disk image",
        "evidence",
        "investigation",
        "case file",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Network Scanning",
      description: "Network discovery tools",
      query:
        'intext:"nmap" OR intext:"network scan" OR intext:"port scan" OR intext:"vulnerability scan" OR intext:"network discovery"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "sh", "rb", "ps1", "txt", "md", "json", "xml", "csv"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "nmap",
        "network scan",
        "port scan",
        "vulnerability scan",
        "network discovery",
        "reconnaissance",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Web Exploitation",
      description: "Web app security tools",
      query:
        'intext:"sql injection" OR intext:"xss" OR intext:"csrf" OR intext:"web vulnerability" OR intext:"web security" OR intext:"owasp"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "php", "js", "rb", "txt", "md", "html", "json", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "sql injection",
        "xss",
        "csrf",
        "web vulnerability",
        "web security",
        "owasp",
        "web app",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Wireless Hacking",
      description: "WiFi security tools",
      query:
        'intext:"wifi hack" OR intext:"aircrack" OR intext:"wireshark" OR intext:"wireless security" OR intext:"802.11" OR intext:"wpa"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "sh", "c", "cpp", "txt", "md", "pcap", "cap", "log"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "wifi hack",
        "aircrack",
        "wireshark",
        "wireless security",
        "802.11",
        "wpa",
        "wep",
        "bluetooth",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Social Engineering",
      description: "Social engineering tools",
      query:
        'intext:"social engineering" OR intext:"phishing" OR intext:"pretexting" OR intext:"baiting" OR intext:"quid pro quo"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "html",
        "php",
        "js",
        "txt",
        "md",
        "json",
        "xml",
        "docx",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "social engineering",
        "phishing",
        "pretexting",
        "baiting",
        "quid pro quo",
        "manipulation",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Cryptography",
      description: "Crypto tools & algorithms",
      query:
        'intext:"cryptography" OR intext:"encryption" OR intext:"hash" OR intext:"cipher" OR intext:"crypto" OR intext:"aes"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "c", "cpp", "java", "txt", "md", "js", "php", "rb"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "cryptography",
        "encryption",
        "hash",
        "cipher",
        "crypto",
        "aes",
        "rsa",
        "sha",
        "md5",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Steganography",
      description: "Hidden data tools",
      query:
        'intext:"steganography" OR intext:"hidden data" OR intext:"lsb" OR intext:"stego" OR intext:"hidden message"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "c", "cpp", "java", "txt", "md", "exe", "dll", "bin"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "steganography",
        "hidden data",
        "lsb",
        "stego",
        "hidden message",
        "covert channel",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Binary Analysis",
      description: "Binary analysis tools",
      query:
        'intext:"binary analysis" OR intext:"disassembly" OR intext:"debugger" OR intext:"binary exploitation" OR intext:"pwn"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "exe",
        "dll",
        "bin",
        "asm",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "binary analysis",
        "disassembly",
        "debugger",
        "binary exploitation",
        "pwn",
        "buffer overflow",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Mobile Security",
      description: "Mobile app security",
      query:
        'intext:"mobile security" OR intext:"android hack" OR intext:"ios security" OR intext:"mobile forensics" OR intext:"app security"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "java",
        "kt",
        "swift",
        "txt",
        "md",
        "apk",
        "ipa",
        "dex",
        "smali",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "mobile security",
        "android hack",
        "ios security",
        "mobile forensics",
        "app security",
        "apk",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "IoT Security",
      description: "Internet of Things security",
      query:
        'intext:"iot security" OR intext:"smart device" OR intext:"firmware" OR intext:"embedded security" OR intext:"device hacking"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "hex",
        "elf",
        "firmware",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "iot security",
        "smart device",
        "firmware",
        "embedded security",
        "device hacking",
        "sensor",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Car Hacking",
      description: "Automotive security research",
      query:
        'intext:"car hacking" OR intext:"automotive security" OR intext:"can bus" OR intext:"vehicle security" OR intext:"car security"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "txt",
        "md",
        "pcap",
        "log",
        "bin",
        "hex",
        "can",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "car hacking",
        "automotive security",
        "can bus",
        "vehicle security",
        "car security",
        "ecu",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Hardware Hacking",
      description: "Hardware security tools",
      query:
        'intext:"hardware hacking" OR intext:"firmware" OR intext:"jtag" OR intext:"hardware security" OR intext:"chip hacking"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "hex",
        "firmware",
        "bitstream",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "hardware hacking",
        "firmware",
        "jtag",
        "hardware security",
        "chip hacking",
        "fpga",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Radio Hacking",
      description: "RF & radio security",
      query:
        'intext:"radio hack" OR intext:"rf security" OR intext:"sdr" OR intext:"radio frequency" OR intext:"wireless hacking"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "grc",
        "log",
        "bin",
        "hex",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "radio hack",
        "rf security",
        "sdr",
        "radio frequency",
        "wireless hacking",
        "hackrf",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Satellite Hacking",
      description: "Satellite security research",
      query:
        'intext:"satellite hack" OR intext:"gps security" OR intext:"satcom" OR intext:"satellite security" OR intext:"gps hacking"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "log",
        "hex",
        "sat",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "satellite hack",
        "gps security",
        "satcom",
        "satellite security",
        "gps hacking",
        "gnss",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Drone Security",
      description: "Drone hacking tools",
      query:
        'intext:"drone hack" OR intext:"uav security" OR intext:"drone control" OR intext:"drone security" OR intext:"uav hacking"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "log",
        "hex",
        "drone",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "drone hack",
        "uav security",
        "drone control",
        "drone security",
        "uav hacking",
        "quadcopter",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Blockchain Security",
      description: "Blockchain hacking tools",
      query:
        'intext:"blockchain hack" OR intext:"smart contract" OR intext:"defi security" OR intext:"crypto security" OR intext:"web3 security"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "js", "sol", "txt", "md", "json", "yaml", "yml", "ts"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "blockchain hack",
        "smart contract",
        "defi security",
        "crypto security",
        "web3 security",
        "solidity",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "AI Security",
      description: "AI/ML security research",
      query:
        'intext:"ai security" OR intext:"machine learning hack" OR intext:"adversarial" OR intext:"ml security" OR intext:"ai attack"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "ipynb",
        "txt",
        "md",
        "json",
        "pkl",
        "h5",
        "onnx",
        "tflite",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "ai security",
        "machine learning hack",
        "adversarial",
        "ml security",
        "ai attack",
        "neural network",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Quantum Security",
      description: "Quantum computing security",
      query:
        'intext:"quantum security" OR intext:"post quantum" OR intext:"quantum hack" OR intext:"quantum cryptography" OR intext:"qkd"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "qasm",
        "bin",
        "hex",
        "qiskit",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "quantum security",
        "post quantum",
        "quantum hack",
        "quantum cryptography",
        "qkd",
        "qubit",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Bug Bounty",
      description: "Bug bounty reports",
      query:
        'intext:"bug bounty" OR intext:"vulnerability report" OR intext:"security disclosure" OR intext:"responsible disclosure" OR intext:"hackerone"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "hackerone.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["md", "txt", "pdf", "html", "json", "xml", "docx", "rtf"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "bug bounty",
        "vulnerability report",
        "security disclosure",
        "responsible disclosure",
        "hackerone",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Zero Day",
      description: "Zero-day exploits",
      query:
        'intext:"zero day" OR intext:"0day" OR intext:"unpatched vulnerability" OR intext:"zero day exploit" OR intext:"0day exploit"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "exe",
        "dll",
        "shellcode",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "zero day",
        "0day",
        "unpatched vulnerability",
        "zero day exploit",
        "0day exploit",
        "unpatched",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Ransomware",
      description: "Ransomware analysis",
      query:
        'intext:"ransomware" OR intext:"crypto locker" OR intext:"file encryption" OR intext:"ransom note" OR intext:"crypto virus"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "exe",
        "dll",
        "bin",
        "encrypted",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "ransomware",
        "crypto locker",
        "file encryption",
        "ransom note",
        "crypto virus",
        "extortion",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "APT Tools",
      description: "Advanced Persistent Threat tools",
      query:
        'intext:"apt" OR intext:"advanced persistent threat" OR intext:"nation state" OR intext:"state sponsored" OR intext:"apt group"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "c",
        "cpp",
        "java",
        "txt",
        "md",
        "bin",
        "exe",
        "dll",
        "shellcode",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "apt",
        "advanced persistent threat",
        "nation state",
        "state sponsored",
        "apt group",
        "threat actor",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "OSINT Tools",
      description: "Open Source Intelligence",
      query:
        'intext:"osint" OR intext:"open source intelligence" OR intext:"information gathering" OR intext:"reconnaissance" OR intext:"intel gathering"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "sh", "rb", "txt", "md", "json", "xml", "yml", "yaml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "osint",
        "open source intelligence",
        "information gathering",
        "reconnaissance",
        "intel gathering",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Threat Intel",
      description: "Threat intelligence feeds",
      query:
        'intext:"threat intelligence" OR intext:"ioc" OR intext:"indicators of compromise" OR intext:"threat feed" OR intext:"cyber threat"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "json",
        "csv",
        "txt",
        "md",
        "xml",
        "yaml",
        "yml",
        "stix",
        "taxii",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "threat intelligence",
        "ioc",
        "indicators of compromise",
        "threat feed",
        "cyber threat",
        "malware",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Incident Response",
      description: "Incident response tools",
      query:
        'intext:"incident response" OR intext:"ir tools" OR intext:"forensics toolkit" OR intext:"response playbook" OR intext:"ir plan"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["py", "sh", "ps1", "txt", "md", "bat", "json", "yml", "yaml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "incident response",
        "ir tools",
        "forensics toolkit",
        "response playbook",
        "ir plan",
        "response",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Compliance Tools",
      description: "Security compliance tools",
      query:
        'intext:"compliance" OR intext:"audit" OR intext:"regulatory" OR intext:"gdpr" OR intext:"sox" OR intext:"pci dss"',
      category: "Security",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: [
        "py",
        "sh",
        "txt",
        "md",
        "json",
        "yaml",
        "yml",
        "pdf",
        "docx",
        "xlsx",
      ],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
        "-inurl:help",
        "-inurl:docs",
      ],
      keywords: [
        "compliance",
        "audit",
        "regulatory",
        "gdpr",
        "sox",
        "pci dss",
        "hipaa",
        "iso",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Malware Forensics",
      description: "Malware analysis and investigation",
      query:
        'intext:"malware forensics" OR intext:"virus analysis" OR intext:"trojan investigation" OR intext:"malware report"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
      ],
      filetypes: ["py", "c", "cpp", "java", "txt", "md", "pdf", "doc"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "malware forensics",
        "virus analysis",
        "trojan investigation",
        "malware report",
        "malware analysis",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Timeline Analysis",
      description: "Event timeline reconstruction",
      query:
        'intext:"timeline analysis" OR intext:"event reconstruction" OR intext:"chronology" OR intext:"timeline"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
      ],
      filetypes: ["py", "c", "cpp", "java", "txt", "md", "pdf", "doc", "csv"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "timeline analysis",
        "event reconstruction",
        "chronology",
        "timeline",
        "event timeline",
        "forensic timeline",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Artifact Analysis",
      description: "Digital artifact examination",
      query:
        'intext:"artifact analysis" OR intext:"digital artifacts" OR intext:"evidence artifacts" OR intext:"forensic artifacts"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
      ],
      filetypes: ["py", "c", "cpp", "java", "txt", "md", "pdf", "doc"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "artifact analysis",
        "digital artifacts",
        "evidence artifacts",
        "forensic artifacts",
        "digital evidence",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Report Generation",
      description: "Forensic report templates",
      query:
        'intext:"forensic report" OR intext:"investigation report" OR intext:"case report" OR intext:"evidence report"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "drive.google.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "md", "html", "rtf"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "forensic report",
        "investigation report",
        "case report",
        "evidence report",
        "forensic documentation",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Legal Research",
      description: "Legal documents and cases",
      query:
        'intext:"legal case" OR intext:"court document" OR intext:"legal precedent" OR intext:"case law"',
      category: "Research",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "onedrive.live.com",
        "pastebin.com",
        "github.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "html", "rtf"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "legal case",
        "court document",
        "legal precedent",
        "case law",
        "legal research",
        "judicial",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Academic Research",
      description: "Academic papers and studies",
      query:
        'intext:"academic paper" OR intext:"research study" OR intext:"scholarly article" OR intext:"peer reviewed"',
      category: "Research",
      sites: [
        "arxiv.org",
        "researchgate.net",
        "academia.edu",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "tex", "md"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "academic paper",
        "research study",
        "scholarly article",
        "peer reviewed",
        "academic research",
        "scholarly",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Market Research",
      description: "Market analysis and reports",
      query:
        'intext:"market research" OR intext:"market analysis" OR intext:"industry report" OR intext:"market study"',
      category: "Research",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "onedrive.live.com",
        "pastebin.com",
        "github.com",
      ],
      filetypes: ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "market research",
        "market analysis",
        "industry report",
        "market study",
        "market intelligence",
        "business research",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Competitive Intelligence",
      description: "Competitor analysis and intelligence",
      query:
        'intext:"competitive intelligence" OR intext:"competitor analysis" OR intext:"market intelligence" OR intext:"business intelligence"',
      category: "Research",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "onedrive.live.com",
        "pastebin.com",
        "github.com",
      ],
      filetypes: ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv", "ppt"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "competitive intelligence",
        "competitor analysis",
        "market intelligence",
        "business intelligence",
        "competitive analysis",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Patent Research",
      description: "Patent documents and applications",
      query:
        'intext:"patent" OR intext:"invention" OR intext:"patent application" OR intext:"patent document"',
      category: "Research",
      sites: [
        "google.com/patents",
        "uspto.gov",
        "drive.google.com",
        "dropbox.com",
        "github.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "html", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "patent",
        "invention",
        "patent application",
        "patent document",
        "intellectual property",
        "patent research",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Technical Documentation",
      description: "Technical manuals and guides",
      query:
        'intext:"technical manual" OR intext:"user guide" OR intext:"technical documentation" OR intext:"system manual"',
      category: "Research",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "md", "html", "rtf"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "technical manual",
        "user guide",
        "technical documentation",
        "system manual",
        "technical guide",
        "documentation",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Data Breach Research",
      description: "Data breach information and analysis",
      query:
        'intext:"data breach" OR intext:"security breach" OR intext:"breach report" OR intext:"compromised data"',
      category: "Research",
      sites: [
        "github.com",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
        "hackerone.com",
      ],
      filetypes: ["txt", "csv", "json", "xml", "pdf", "doc", "md"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "data breach",
        "security breach",
        "breach report",
        "compromised data",
        "data leak",
        "breach analysis",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Vulnerability Research",
      description: "Security vulnerability information",
      query:
        'intext:"vulnerability" OR intext:"security flaw" OR intext:"bug report" OR intext:"security advisory"',
      category: "Research",
      sites: [
        "github.com",
        "gitlab.com",
        "bitbucket.org",
        "pastebin.com",
        "nvd.nist.gov",
      ],
      filetypes: ["txt", "md", "json", "xml", "pdf", "doc"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "vulnerability",
        "security flaw",
        "bug report",
        "security advisory",
        "security research",
        "vuln research",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Threat Intel",
      description: "Cyber threat information",
      query:
        'intext:"threat intelligence" OR intext:"cyber threat" OR intext:"threat report" OR intext:"security threat"',
      category: "Research",
      sites: [
        "github.com",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
        "alienvault.com",
      ],
      filetypes: ["txt", "csv", "json", "xml", "pdf", "doc", "md"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "threat intelligence",
        "cyber threat",
        "threat report",
        "security threat",
        "threat research",
        "cyber intelligence",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Incident Reports",
      description: "Security incident documentation",
      query:
        'intext:"incident report" OR intext:"security incident" OR intext:"breach incident" OR intext:"security event"',
      category: "Research",
      sites: [
        "github.com",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
        "hackerone.com",
      ],
      filetypes: ["txt", "md", "pdf", "doc", "docx", "html"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "incident report",
        "security incident",
        "breach incident",
        "security event",
        "incident documentation",
        "security report",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "OSINT People Search",
      description: "Find information about specific people",
      query:
        'intext:"@gmail.com" OR intext:"@yahoo.com" OR intext:"phone number" OR intext:"address" OR intext:"resume"',
      category: "Forensics",
      sites: [
        "linkedin.com",
        "facebook.com",
        "twitter.com",
        "instagram.com",
        "pastebin.com",
        "github.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "html", "json"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "name",
        "email",
        "phone",
        "address",
        "resume",
        "profile",
        "contact",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Social Media Forensics",
      description: "Social media data and profiles",
      query:
        'intext:"facebook.com" OR intext:"twitter.com" OR intext:"instagram.com" OR intext:"linkedin.com"',
      category: "Forensics",
      sites: [
        "facebook.com",
        "twitter.com",
        "instagram.com",
        "linkedin.com",
        "reddit.com",
        "tumblr.com",
      ],
      filetypes: ["html", "json", "txt", "csv", "pdf", "doc"],
      exclusions: ["-site:stackoverflow.com", "-inurl:admin", "-inurl:login"],
      keywords: [
        "profile",
        "post",
        "comment",
        "message",
        "timeline",
        "activity",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Digital Footprint",
      description: "Online presence and digital traces",
      query:
        'intext:"username" OR intext:"handle" OR intext:"online presence" OR intext:"digital footprint"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "reddit.com",
        "twitter.com",
        "medium.com",
        "dev.to",
      ],
      filetypes: ["txt", "md", "json", "html", "pdf", "doc"],
      exclusions: ["-site:stackoverflow.com", "-inurl:admin"],
      keywords: [
        "username",
        "handle",
        "alias",
        "nickname",
        "online identity",
        "digital presence",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Location Tracking",
      description: "Geolocation and mapping data",
      query:
        'intext:"gps coordinates" OR intext:"latitude" OR intext:"longitude" OR intext:"location data"',
      category: "Forensics",
      sites: [
        "google.com/maps",
        "openstreetmap.org",
        "github.com",
        "pastebin.com",
        "drive.google.com",
      ],
      filetypes: ["kml", "gpx", "json", "csv", "txt", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "gps",
        "coordinates",
        "latitude",
        "longitude",
        "location",
        "map",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Network Forensics",
      description: "Network traffic and packet analysis",
      query:
        'intext:"pcap" OR intext:"packet capture" OR intext:"network traffic" OR intext:"wireshark"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["pcap", "cap", "txt", "log", "csv", "json"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "network",
        "traffic",
        "packet",
        "capture",
        "analysis",
        "forensics",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Mobile Forensics",
      description: "Mobile device data extraction",
      query:
        'intext:"mobile forensics" OR intext:"android extraction" OR intext:"ios backup" OR intext:"mobile data"',
      category: "Forensics",
      sites: [
        "github.com",
        "gitlab.com",
        "pastebin.com",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["sqlite", "db", "plist", "xml", "json", "txt"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "mobile",
        "android",
        "ios",
        "backup",
        "extraction",
        "forensics",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Financial Forensics",
      description: "Financial records and transactions",
      query:
        'intext:"bank statement" OR intext:"transaction history" OR intext:"financial records" OR intext:"account statement"',
      category: "Forensics",
      sites: [
        "drive.google.com",
        "dropbox.com",
        "onedrive.live.com",
        "pastebin.com",
      ],
      filetypes: ["pdf", "xls", "xlsx", "csv", "txt", "ofx"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "financial",
        "bank",
        "transaction",
        "statement",
        "account",
        "records",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Academic Research Papers",
      description: "Scholarly articles and research",
      query:
        'intext:"research paper" OR intext:"academic study" OR intext:"scholarly article" OR intext:"peer reviewed"',
      category: "Research",
      sites: [
        "arxiv.org",
        "researchgate.net",
        "academia.edu",
        "scholar.google.com",
      ],
      filetypes: ["pdf", "doc", "docx", "tex", "txt", "md"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "research",
        "academic",
        "scholarly",
        "paper",
        "study",
        "publication",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Government Documents",
      description: "Official government records",
      query:
        'intext:"government document" OR intext:"public record" OR intext:"official report" OR intext:"filing"',
      category: "Research",
      sites: [
        "gov.uk",
        "usa.gov",
        "canada.ca",
        "europa.eu",
        "drive.google.com",
      ],
      filetypes: ["pdf", "doc", "docx", "txt", "html", "xml"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "government",
        "official",
        "public",
        "record",
        "document",
        "report",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Corporate Research",
      description: "Company information and reports",
      query:
        'intext:"annual report" OR intext:"financial statement" OR intext:"corporate filing" OR intext:"company profile"',
      category: "Research",
      sites: [
        "sec.gov",
        "companieshouse.gov.uk",
        "drive.google.com",
        "dropbox.com",
      ],
      filetypes: ["pdf", "xls", "xlsx", "doc", "docx", "txt"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "corporate",
        "company",
        "report",
        "financial",
        "filing",
        "profile",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
    {
      name: "Technical Research",
      description: "Technical documentation and specs",
      query:
        'intext:"technical specification" OR intext:"system documentation" OR intext:"architecture diagram" OR intext:"technical manual"',
      category: "Research",
      sites: [
        "github.com",
        "gitlab.com",
        "docs.google.com",
        "drive.google.com",
      ],
      filetypes: ["pdf", "md", "txt", "doc", "docx", "drawio"],
      exclusions: [
        "-site:stackoverflow.com",
        "-site:reddit.com",
        "-inurl:admin",
      ],
      keywords: [
        "technical",
        "specification",
        "documentation",
        "manual",
        "guide",
        "architecture",
      ],
      timeframes: ["2020", "2021", "2022", "2023", "2024", "2025"],
      operators: [
        "intext:",
        "inurl:",
        "site:",
        "filetype:",
        "intitle:",
        "after:",
      ],
    },
  ];

  // Tips with short example
  const searchTips = [
    { text: "Title search", example: 'intitle:"index of"' },
    { text: "URL filter", example: "inurl:backup" },
    { text: "File type", example: "filetype:pdf" },
    { text: "In page text", example: 'intext:"confidential"' },
    { text: "Combine", example: "(report OR review)" },
    { text: "Exclude", example: "-inurl:login -site:github.com" },
    { text: "Exact phrase", example: '"security audit"' },
    { text: "Scope", example: "site:drive.google.com" },
  ];

  const categories = [
    "All",
    ...Array.from(new Set(searchTemplates.map((t) => t.category))),
  ];

  const filteredTemplates = searchTemplates
    .filter((template) => {
      const matchesSearchTerm =
        template.name
          .toLowerCase()
          .includes(templateSearchTerm.toLowerCase()) ||
        template.description
          .toLowerCase()
          .includes(templateSearchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || template.category === selectedCategory;
      return matchesSearchTerm && matchesCategory;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const generateAdvancedQuery = (baseQuery, template = null) => {
    if (!baseQuery.trim() && !template) return "";

    let query = baseQuery.trim();

    // If no base query but template is selected, use template as base
    if (!query && template) {
      query = template.query;
    }
    // If both exist, combine them
    else if (query && template) {
      query = `${query} ${template.query}`;
    }

    // Add custom template fields if available
    if (template && templateFields.sites.trim()) {
      const sites = templateFields.sites
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s);
      if (sites.length > 0) {
        const siteQuery = sites.map((site) => `site:${site}`).join(" OR ");
        query += ` (${siteQuery})`;
      }
    }

    if (template && templateFields.filetypes.trim()) {
      const types = templateFields.filetypes
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      if (types.length > 0) {
        const filetypeQuery = types
          .map((type) => `filetype:${type}`)
          .join(" OR ");
        query += ` (${filetypeQuery})`;
      }
    }

    if (template && templateFields.exclusions.trim()) {
      const exclusions = templateFields.exclusions
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e);
      if (exclusions.length > 0) {
        query += ` ${exclusions.join(" ")}`;
      }
    }

    if (template && templateFields.keywords.trim()) {
      const keywords = templateFields.keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);
      if (keywords.length > 0) {
        const keywordQuery = keywords
          .map((keyword) => `intext:"${keyword}"`)
          .join(" OR ");
        query += ` (${keywordQuery})`;
      }
    }

    if (template && templateFields.timeframes.trim()) {
      const timeframes = templateFields.timeframes
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      if (timeframes.length > 0) {
        const timeframeQuery = timeframes
          .map((timeframe) => `after:${timeframe}`)
          .join(" OR ");
        query += ` (${timeframeQuery})`;
      }
    }

    if (template && templateFields.operators.trim()) {
      const operators = templateFields.operators
        .split(",")
        .map((o) => o.trim())
        .filter((o) => o);
      if (operators.length > 0) {
        query += ` ${operators.join(" ")}`;
      }
    }

    if (template && templateFields.customQuery.trim()) {
      query += ` ${templateFields.customQuery}`;
    }

    // Add filetype if not already present and no custom filetypes specified
    if (
      !query.includes("filetype:") &&
      !(!template || !templateFields.filetypes.trim())
    ) {
      query +=
        " (filetype:pdf OR filetype:doc OR filetype:xls OR filetype:txt)";
    }

    return query;
  };

  const handleGenerateQuery = () => {
    if (searchQuery.trim() || selectedTemplate) {
      const advancedQuery = generateAdvancedQuery(
        searchQuery,
        selectedTemplate
      );
      setGeneratedQuery(advancedQuery);
      setShowGeneratedQuery(true);

      const newHistoryItem = {
        id: Date.now(),
        originalQuery: searchQuery || selectedTemplate?.name || "Template",
        generatedQuery: advancedQuery,
        timestamp: new Date().toLocaleString(),
      };
      setSearchHistory((prev) => [newHistoryItem, ...prev.slice(0, 9)]);
    }
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setGeneratedQuery(""); // Clear generated query when template is selected
    setShowGeneratedQuery(false); // Hide query section
    setTemplateFields({
      sites: template.sites?.join(", ") || "",
      filetypes: template.filetypes?.join(", ") || "",
      exclusions: template.exclusions?.join(", ") || "",
      keywords: template.keywords?.join(", ") || "",
      timeframes: template.timeframes?.join(", ") || "",
      operators: template.operators?.join(", ") || "",
      customQuery: "",
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedQuery(true);
      setTimeout(() => setCopiedQuery(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const setTemplateFieldsWithHide = (updater) => {
    setTemplateFields(updater);
  };

  const clearAll = () => {
    setSearchQuery("");
    setGeneratedQuery("");
    setShowGeneratedQuery(false);
    setSelectedTemplate(null);
    setTemplateFields({
      sites: "",
      filetypes: "",
      exclusions: "",
      keywords: "",
      timeframes: "",
      operators: "",
      customQuery: "",
    });
  };

  const removeFromHistory = (id) => {
    setSearchHistory((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div className="digger-container">
      <div className="digger-header">
        <h1 className="digger-title">Search Query</h1>
      </div>

      <div className="digger-search-container">
        <div className="search-input-group">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <textarea
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              placeholder="Type base query..."
              className="search-input search-textarea"
              rows="1"
              wrap="soft"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearAll}
                className="clear-search-btn"
                title="Clear"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className="search-options">
            <button
              onClick={handleGenerateQuery}
              className="search-btn generate-btn"
              disabled={!searchQuery.trim() && !selectedTemplate}
            >
              Generate
            </button>
          </div>
        </div>

        {/* Template Fields - shown when template is selected */}
        {selectedTemplate && (
          <div className="template-fields-container">
            <h4 className="template-fields-title">
              Customize {selectedTemplate.name} Template
            </h4>

            <div className="template-fields-grid">
              <div className="template-field-input">
                <label className="field-label">Sites to search:</label>
                <input
                  type="text"
                  value={templateFields.sites}
                  onChange={(e) =>
                    setTemplateFieldsWithHide((prev) => ({
                      ...prev,
                      sites: e.target.value,
                    }))
                  }
                  placeholder="e.g., drive.google.com, dropbox.com"
                  className="template-input"
                />
                <small className="field-help">
                  Separate multiple sites with commas
                </small>
              </div>

              <div className="template-field-input">
                <label className="field-label">File types:</label>
                <input
                  type="text"
                  value={templateFields.filetypes}
                  onChange={(e) =>
                    setTemplateFieldsWithHide((prev) => ({
                      ...prev,
                      filetypes: e.target.value,
                    }))
                  }
                  placeholder="e.g., pdf, doc, zip"
                  className="template-input"
                />
                <small className="field-help">
                  Separate multiple types with commas
                </small>
              </div>

              <div className="template-field-input">
                <label className="field-label">Exclusions:</label>
                <input
                  type="text"
                  value={templateFields.exclusions}
                  onChange={(e) =>
                    setTemplateFieldsWithHide((prev) => ({
                      ...prev,
                      exclusions: e.target.value,
                    }))
                  }
                  placeholder="e.g., -site:github.com, -inurl:admin"
                  className="template-input"
                />
                <small className="field-help">
                  Separate multiple exclusions with commas
                </small>
              </div>
            </div>
          </div>
        )}

        {showTips && (
          <div className="search-tips">
            <div className="tips-header">
              <h3>Search tips</h3>
              <button
                onClick={() => setShowTips(false)}
                className="close-tips-btn"
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="tips-content">
              {searchTips.map((tip, index) => (
                <div key={index} className="tip-item">
                  <span className="tip-bullet">✦</span>
                  <span className="tip-text">{tip.text}</span>
                  <code className="tip-example">{tip.example}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {showGeneratedQuery && generatedQuery && (
          <div className="generated-query-container">
            <div className="generated-query-header">
              <h3>Generated Query</h3>
              <button
                onClick={() => copyToClipboard(generatedQuery)}
                className="copy-query-btn"
                title="Copy"
              >
                {copiedQuery ? (
                  "Copied"
                ) : (
                  <>
                    <FaCopy /> Copy
                  </>
                )}
              </button>
            </div>
            <div className="generated-query-content">
              <code className="query-text">{generatedQuery}</code>
            </div>
            <div className="query-actions">
              <button
                onClick={() =>
                  window.open(
                    `https://www.google.com/search?q=${encodeURIComponent(
                      generatedQuery
                    )}`,
                    "_blank"
                  )
                }
                className="search-google-btn"
              >
                Open in Google
              </button>
              <button
                onClick={() =>
                  window.open(
                    `https://www.bing.com/search?q=${encodeURIComponent(
                      generatedQuery
                    )}`,
                    "_blank"
                  )
                }
                className="search-bing-btn"
              >
                Open in Bing
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="templates-section">
        <h3>Templates</h3>
        {/* Template Search and Filter */}
        <div className="template-filters">
          <div className="template-search">
            <input
              type="text"
              placeholder="Search templates..."
              value={templateSearchTerm}
              onChange={(e) => setTemplateSearchTerm(e.target.value)}
              className="template-search-input"
            />
          </div>

          <div className="template-category-filter">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="template-stats">
          <span className="template-count">
            Showing {filteredTemplates.length} of {searchTemplates.length}{" "}
            templates
          </span>
        </div>

        <div className="templates-grid">
          {filteredTemplates.map((template, index) => (
            <div
              key={index}
              className={`template-card ${
                selectedTemplate?.name === template.name
                  ? "template-selected"
                  : ""
              }`}
              onClick={() => handleTemplateClick(template)}
            >
              <div className="template-header">
                <h4 className="template-name">{template.name}</h4>
                <span className="template-category">{template.category}</span>
              </div>
              <p className="template-description">{template.description}</p>
              <div className="template-query-preview">
                <code>
                  {template.query.length > 80
                    ? template.query.substring(0, 80) + "..."
                    : template.query}
                </code>
              </div>
            </div>
          ))}
        </div>
      </div>

      {searchHistory.length > 0 && (
        <div className="history-section">
          <h3>Recent</h3>
          <div className="history-list">
            {searchHistory.map((item) => (
              <div key={item.id} className="history-item">
                <div className="history-content">
                  <div>
                    <div className="history-title-row">
                      <div className="history-query">
                        <strong>Base:</strong> {item.originalQuery}
                      </div>
                      <div className="history-actions">
                        <button
                          onClick={() => copyToClipboard(item.generatedQuery)}
                          className="history-copy-btn"
                          title="Copy"
                        >
                          <FaCopy />
                        </button>
                        <button
                          onClick={() => removeFromHistory(item.id)}
                          className="history-remove-btn"
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="history-generated">
                    <strong>Query:</strong>
                    <code className="history-query-text">
                      {item.generatedQuery}
                    </code>
                  </div>
                  <div className="history-timestamp">{item.timestamp}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchQueryGenerator;
